import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Truck, Package, DollarSign, Clock, MapPin, Phone, CheckCircle, Navigation, AlertCircle, TrendingUp, Calendar } from "lucide-react";
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/hooks/useAuth';

export default function DelegateDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    todayShipments: 0,
    inTransit: 0,
    delivered: 0,
    collected: 0,
    successRate: 0
  });
  const [recentShipments, setRecentShipments] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState<number | null>(null);

  useEffect(() => {
    fetchDelegateData();
  }, [user]);

  const fetchDelegateData = async () => {
if (!user) return;  
  try {
      setLoading(true);
const delegateId = user?.delegateId || user?.id;

const shipmentsRes = await api.get(`/api/shipments/delegate/${delegateId}`);

const allShipments =
  shipmentsRes.data?.data ||
  shipmentsRes.data?.items ||
  [];
      
      const today = new Date().toDateString();
      const todayShipments = allShipments.filter((s: any) => 
        s.createdAt && new Date(s.createdAt).toDateString() === today
      );

      const inTransit = allShipments.filter((s: any) => 
        (s.statusName || '').toLowerCase().includes('transit') || 
        (s.statusName || '').toLowerCase().includes('طريق') ||
        (s.statusName || '').toLowerCase().includes('out')
      );

      const delivered = allShipments.filter((s: any) => 
        (s.statusName || '').toLowerCase().includes('deliver') || 
        (s.statusName || '').toLowerCase().includes('توصيل')
      );

      const collected = allShipments.reduce((sum: number, s: any) => 
        sum + (s.codAmount || 0), 0
      );

      const successRate = allShipments.length > 0 
        ? Math.round((delivered.length / allShipments.length) * 100)
        : 0;

      setStats({
        todayShipments: todayShipments.length,
        inTransit: inTransit.length,
        delivered: delivered.length,
        collected,
        successRate
      });

      // أحدث 5 شحنات للعرض
      const activeShipments = [...inTransit, ...todayShipments].slice(0, 5);
      setRecentShipments(activeShipments.length > 0 ? activeShipments : allShipments.slice(0, 5));
    } catch (error) {
      console.error('Error fetching delegate data:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateShipmentStatus = async (shipmentId: number, newStatus: string) => {
    setUpdatingId(shipmentId);
    try {
      const delegateId = user?.delegateId;
      if (!delegateId) {
        alert('حسابك غير مربوط بسجل مندوب. تواصل مع الإدارة.');
        return;
      }
      const res = await api.put(`/api/delegate-dashboard/${delegateId}/shipments/${shipmentId}/status`, { status: newStatus });
      if (res?.data?.success === false) {
        alert(res.data.message || 'تعذر تحديث الحالة');
      }
      await fetchDelegateData();
    } catch (error: any) {
      console.error('Error updating status:', error);
      alert(error?.response?.data?.message || 'حدث خطأ أثناء تحديث الحالة');
    } finally {
      setUpdatingId(null);
    }
  };

  const getStatusColor = (status: string) => {
    const s = (status || '').toLowerCase();
    if (s.includes('deliver') || s.includes('توصيل')) return 'bg-green-100 text-green-800';
    if (s.includes('transit') || s.includes('طريق') || s.includes('out')) return 'bg-blue-100 text-blue-800';
    if (s.includes('pending') || s.includes('معلق')) return 'bg-yellow-100 text-yellow-800';
    if (s.includes('return') || s.includes('رجع')) return 'bg-red-100 text-red-800';
    return 'bg-gray-100 text-gray-800';
  };

  const getStatusActions = (status: string, shipmentId: number) => {
    const s = (status || '').toLowerCase();
    if (s.includes('pending')) {
      return (
        <Button 
          size="sm" 
          onClick={() => updateShipmentStatus(shipmentId, 'Out for Delivery')}
          disabled={updatingId === shipmentId}
          className="bg-blue-600 hover:bg-blue-700 text-xs"
        >
          {updatingId === shipmentId ? '...' : 'بدأ التوصيل'}
        </Button>
      );
    }
    if (s.includes('out') || s.includes('transit') || s.includes('طريق')) {
      return (
        <Button 
          size="sm" 
          onClick={() => updateShipmentStatus(shipmentId, 'Delivered')}
          disabled={updatingId === shipmentId}
          className="bg-green-600 hover:bg-green-700 text-xs"
        >
          {updatingId === shipmentId ? '...' : 'تم التوصيل'}
        </Button>
      );
    }
    return null;
  };

  return (
    <div className="p-6 space-y-6" dir="rtl">
      {/* الرأس */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{`مرحباً بك، ${user?.fullName || user?.username || 'مندوبنا المميز'}`}</h1>
          <p className="text-muted-foreground mt-1 flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            {new Date().toLocaleDateString('ar-EG', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => navigate('/delegate/tracking')} variant="outline" className="gap-2">
            <Navigation className="w-4 h-4" />
            {'تحديث موقعي'}
          </Button>
          <Button onClick={() => navigate('/notifications')} variant="outline" className="gap-2">
            <AlertCircle className="w-4 h-4" />
            {'الإشعارات'}
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p>{'جاري التحميل...'}</p>
        </div>
      ) : (
        <>
          {/* بطاقات الإحصائيات */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <Card className="bg-gradient-to-br from-blue-50 to-blue-100">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium">{'شحنات اليوم'}</CardTitle>
                <Package className="w-4 h-4 text-blue-600" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-blue-700">{stats.todayShipments}</div>
                <p className="text-xs text-blue-600 mt-1">{'شحنة اليوم'}</p>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-yellow-50 to-yellow-100">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium">{'قيد التوصيل'}</CardTitle>
                <Truck className="w-4 h-4 text-yellow-600" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-yellow-700">{stats.inTransit}</div>
                <p className="text-xs text-yellow-600 mt-1">{'في الطريق'}</p>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-green-50 to-green-100">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium">{'تم التوصيل'}</CardTitle>
                <CheckCircle className="w-4 h-4 text-green-600" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-green-700">{stats.delivered}</div>
                <p className="text-xs text-green-600 mt-1">{'شحنة موصلة'}</p>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium">{'تم التحصيل'}</CardTitle>
                <DollarSign className="w-4 h-4 text-emerald-600" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-emerald-700">{stats.collected.toFixed(0)}</div>
                <p className="text-xs text-emerald-600 mt-1">{'ج.م محصل'}</p>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-purple-50 to-purple-100">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium">{'نسبة النجاح'}</CardTitle>
                <TrendingUp className="w-4 h-4 text-purple-600" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-purple-700">{stats.successRate}%</div>
                <p className="text-xs text-purple-600 mt-1">{'من الشحنات'}</p>
              </CardContent>
            </Card>
          </div>

          {/* المهام السريعة */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Button 
              onClick={() => navigate('/delegate/tracking')}
              className="bg-blue-600 hover:bg-blue-700 h-20 flex-col gap-2"
            >
              <Navigation className="w-6 h-6" />
              <span>{'تحديث الموقع'}</span>
            </Button>
            <Button 
onClick={() => navigate('/delegate/shipments')}
              variant="outline"
              className="h-20 flex-col gap-2"
            >
              <Package className="w-6 h-6" />
              <span>{'جميع الشحنات'}</span>
            </Button>
            <Button 
              onClick={() => navigate('/notifications')}
              variant="outline"
              className="h-20 flex-col gap-2"
            >
              <AlertCircle className="w-6 h-6" />
              <span>{'الإشعارات'}</span>
            </Button>
          </div>

          {/* المهام النشطة */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Truck className="w-5 h-5 text-blue-600" />
                {`المهام النشطة (${recentShipments.length})`}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {recentShipments.length === 0 ? (
                <div className="text-center py-12">
                  <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">{'لا توجد مهام حالياً'}</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {recentShipments.map((shipment) => (
                    <div key={shipment.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 transition">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <p className="font-mono font-bold text-blue-700">{shipment.trackingNumber}</p>
                          <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getStatusColor(shipment.statusName)}`}>
                            {shipment.statusName || '-'}
                          </span>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm">
                          <div className="flex items-center gap-2">
                            <Phone className="w-3 h-3 text-gray-400" />
                            <span>{shipment.receiverName}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Phone className="w-3 h-3 text-gray-400" />
                            <a href={`tel:${shipment.receiverPhone}`} className="text-blue-600 hover:underline">
                              {shipment.receiverPhone}
                            </a>
                          </div>
                          <div className="flex items-center gap-2">
                            <MapPin className="w-3 h-3 text-gray-400" />
                            <span className="truncate">{shipment.receiverCity}</span>
                          </div>
                        </div>
                        {(shipment.codAmount || 0) > 0 && (
                          <p className="text-sm font-semibold mt-2 text-green-700">
                            {'مبلغ التحصيل: '}{(shipment.codAmount || 0).toFixed(2)} {'ج.م'}
                          </p>
                        )}
                      </div>
                      <div className="flex flex-col gap-2 mr-4">
                        {getStatusActions(shipment.statusName, shipment.id)}
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => navigate(`/shipments/${shipment.id}`)}
                          className="text-xs"
                        >
                          {'التفاصيل'}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* تنبيهات مهمة */}
          <Card className="bg-gradient-to-l from-blue-50 to-indigo-50 border-blue-200">
            <CardContent className="p-6">
              <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-blue-600" />
                {'تنبيهات مهمة'}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="p-3 bg-white rounded-lg border border-blue-100">
                  <p className="text-sm font-semibold text-blue-900">{'✅ تحديث الموقع'}</p>
                  <p className="text-xs text-blue-700 mt-1">{'حدث موقعك دورياً لاستلام شحنات جديدة'}</p>
                </div>
                <div className="p-3 bg-white rounded-lg border border-green-100">
                  <p className="text-sm font-semibold text-green-900">{'✅ تحصيل المدفوعات'}</p>
                  <p className="text-xs text-green-700 mt-1">{'تأكد من تحصيل مبالغ COD عند التوصيل'}</p>
                </div>
                <div className="p-3 bg-white rounded-lg border border-yellow-100">
                  <p className="text-sm font-semibold text-yellow-900">{'✅ حالة الشحنة'}</p>
                  <p className="text-xs text-yellow-700 mt-1">{'حدث حالة الشحنة فوراً بعد التوصيل'}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
